import styled from "styled-components";
import { FlexGrowProps } from "styled-system";
import Box from "../Box";
import {
  CIcon,
  CIconButton,
  CText,
} from "@carrefour/design-system-components-react";
import Line from "../Line";
import { useAuth } from "../../Auth/AuthProvider";
import { useDispatch, useSelector } from "react-redux";
import { handleSidebar } from "../../slices/sideBarSlice";
import { RootState } from "../../store";
import { handleModeSlice } from "../../slices/modeSlice";
import { useNavigate, useLocation } from "react-router";
import { isEmailInWhitelist } from "../../utils/whiteListUtils";
import {
  isRegionMode,
  isStoreMode,
  isAlertMode,
  isStoreOrAlertMode,
} from "../../utils/modeUtils";
import { useTranslation } from "react-i18next";
import { useKpiVisibility } from "../../kpis/useKpiVisibility";
import {
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { useTerritories } from "../../context/territoriesContext";
import { AlertSerie } from "../../types/alertSerie";
import { ReactComponent as LogOutIcon } from "../../media/logout.svg";

import {
  fetchAlertsWithStoreKey,
  fetchAppVersion,
} from "../../utils/fetcher";
import { Range } from "../../utils/range";
import { useStores } from "../../context/storesContext";
import { AlertStatus } from "../../utils/alertUtils";
import { ReactComponent as CommIcon } from "../../media/communication.svg";
import { ReactComponent as FrIcon } from "../../media/francais.svg";
import { ReactComponent as EngIcon } from "../../media/english.svg";
import { ReactComponent as DutchIcon } from "../../media/dutch.svg";
import CommunicationModal from "../CommunicationModal/CommunicationModal";
import SelectLanguageModal from "../SelectLanguageModal";
import { useModals } from "../../context/modalsContext";
import { useRange } from "../../context/rangeContext";
import {
  useOptionCommercial,
  useOptionRegion,
} from "../../utils/options";
import { showIcon } from "../../utils/iconUtils";
import { getLanguageLabel } from "../../utils/langUtils";
import { isStoreOpen } from "../../utils/storeUtils";
import useDisplayHiddenMenu from "../../context/useDisplayHiddenMenu";
import { useIsCaLiveRoute, useIsDashboardRoute } from "../../context/useIsRouteActive";

type SideBarProps = {
  isVisible: boolean;
} & FlexGrowProps;

const CA_LIVE_ALLOWED_ROLES = [
  "ca_live",
  "admin",
  "adminit",
];

const Text = styled(CText)<{ color?: string }>`
  font-family: system-ui;
  color: ${(props) => props.color};
`;

const SafeText = styled(Text)`
  /* Prevent iOS data detectors from styling the text as a link and making it clickable */
  a[x-apple-data-detectors],
  a {
    color: inherit !important;
    text-decoration: none !important;
    pointer-events: none !important;
  }
`;

const BoxCustom = styled(Box)`
  cursor: pointer;
`;

const IconButtonCustom = styled(CIconButton)<{
  $active?: boolean;
}>`
  color: ${(props) => (props.$active ? "#0970e6" : "")};
`;

const CIconCustom = styled(CIcon)`
  font-size: 1.8rem;
`;

const StyledSideBarBox = styled(Box)`
  /* ============================================================
     DEFAULT / DESKTOP / TABLET
     ------------------------------------------------------------
     DOM (all layouts):

       .side_bar                      <- StyledSideBarBox
         .sidebar-inner               <- position: relative, 100% height
           .sidebar-header
           .sidebar-default-menu      <- main menu (scrolls in mobile portrait)
           .sidebar-bottom            <- SIBLING of the main menu (never a child)
           .sidebar-landscape-menu    <- only displayed in mobile landscape
           .sidebar-landscape-version <- only displayed in mobile landscape

     On desktop/tablet the bottom group keeps its historical behaviour:
     it is absolutely pinned to the bottom edge of .sidebar-inner, which
     is exactly where it used to be when it lived inside the default menu
     (the default menu is flex: 1 and always reached the bottom edge).
     ============================================================ */

  .sidebar-default-menu {
    display: block;
    flex: 1 1 auto;
    min-height: 0;
    position: relative;
  }

  .sidebar-bottom {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
  }

  .sidebar-landscape-menu,
  .sidebar-landscape-version {
    display: none;
  }

  /* ============================================================
     MOBILE LANDSCAPE MENU
     ============================================================ */

  @media (max-height: 560px) and (min-width: 568px) and (orientation: landscape) {
  &:not(.show_side_bar) {
    display: none !important;
  }

  &.show_side_bar {
    display: flex !important;
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    transform: none !important;
    border-radius: 8px !important;
    border-radius: 0 !important;

    width: 75vw !important;
    max-width: 75vw !important;
    height: 100dvh !important;

    z-index: 9999 !important;
    background: #fff !important;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15) !important;
    overflow: hidden !important;
    box-sizing: border-box !important;
  }

  .sidebar-default-menu,
  .sidebar-bottom {
    display: none !important;
  }

  .sidebar-header {
    flex: 0 0 48px !important;
    width: 100% !important;
    height: 48px !important;
    padding: 6px 16px !important;
    box-sizing: border-box !important;
    border-bottom: 1px solid #e5e7eb;
  }

  .sidebar-landscape-menu {
    display: grid !important;

    /* Smaller gap between the two columns */
    grid-template-columns: 1fr 1fr;

    flex: 1 1 auto;
    width: 100%;
    min-width: 0;
    min-height: 0;
    box-sizing: border-box;
    overflow: hidden;
  }

  .sidebar-landscape-left,
  .sidebar-landscape-right {
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
  }

  .sidebar-landscape-left {
    border-right: 1px solid #e5e7eb;
  }

  .sidebar-landscape-right {
    padding: 0 !important;
  }

  .sidebar-landscape-item {
    flex: 0 0 auto;
    min-height: 48px;

    width: 100% !important;

    /* Reduced horizontal spacing */
    padding: 6px 12px !important;

    box-sizing: border-box;
    display: flex !important;
    align-items: center !important;

    cursor: pointer;
    overflow: hidden;
  }

  .sidebar-landscape-left .sidebar-landscape-item {
    padding-left: 12px !important;
    padding-right: 12px !important;
  }

  .sidebar-landscape-right .sidebar-landscape-item {
    padding-left: 12px !important;
    padding-right: 12px !important;
  }

  /* Reduce the gap between icon and text */
  .sidebar-landscape-item svg {
    margin-right: 6px !important;
    flex-shrink: 0;
  }

  .sidebar-landscape-item .sidebar-item-text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .sidebar-landscape-divider {
    flex: 0 0 1px;
    width: calc(100% - 24px);
    margin: 0 12px;
    background: #e5e7eb;
  }

  .sidebar-landscape-logout {
    margin-top: auto !important;
  }

  .sidebar-landscape-version {
    display: flex !important;
    flex: 0 0 36px;

    width: 100%;

    align-items: center;
    justify-content: flex-start;

    padding: 4px 12px !important;

    box-sizing: border-box;
    background: #f5f5f5;

    white-space: nowrap;
    overflow: hidden;
  }

  .sidebar-landscape-version span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .sidebar-landscape-menu span,
  .sidebar-landscape-menu h4 {
    font-size: 0.9rem !important;
  }

  .sidebar-landscape-menu svg {
    flex-shrink: 0;
  }
  }

  /* ============================================================
     MOBILE PORTRAIT MENU
     ------------------------------------------------------------
     Exactly three vertical regions, driven by a grid on
     .sidebar-inner:

       row 1  auto          -> header            (fixed height)
       row 2  minmax(0,1fr) -> main menu         (remaining space, scrolls)
       row 3  auto          -> bottom menu       (fixed height, always visible)

     Why this is robust:
       - The outer .side_bar gets an explicit, definite height
         (100dvh, 100vh fallback). Without a definite height the
         inner "height: 100%" resolves to "auto" and nothing is
         constrained by the viewport.
       - The grid track is minmax(0, 1fr): the 0 minimum removes the
         implicit "min-height: auto" that lets content overflow the
         container instead of scrolling.
       - The bottom menu is a grid row of its own, so it can never be
         scrolled away or pushed below the viewport by the main menu.
     ============================================================ */

  @media (max-width: 567px) and (orientation: portrait) {
    /* Doubled selector (&&) beats a single-class height rule coming
       from the global stylesheet without having to depend on its order.
       Declaration order matters: the dvh line is ignored by browsers
       that do not know dvh, so they keep the vh fallback. */
    && {
      box-sizing: border-box;
      height: 100vh !important;
      height: 100dvh !important;
      max-height: 100vh !important;
      max-height: 100dvh !important;
      min-height: 0 !important;
      overflow: hidden !important;
    }

    .sidebar-inner {
      height: 100%;
      min-height: 0;
      display: grid;
      grid-template-rows: auto minmax(0, 1fr) auto;
      overflow: hidden;
    }

    .sidebar-header {
      grid-row: 1;
      min-height: 0;
    }

    /* Main menu: takes the remaining height, is the ONLY scroller */
    .sidebar-default-menu {
      grid-row: 2;
      display: flex;
      flex-direction: column;
      min-height: 0;
      position: relative;
      overflow-y: auto;
      overflow-x: hidden;
      overscroll-behavior: contain;
      -webkit-overflow-scrolling: touch;
    }

    /* Rows must never be squeezed: they scroll instead of shrinking */
    .sidebar-default-menu > * {
      flex-shrink: 0;
    }

    /* Bottom menu: sibling of the main menu, its own grid row,
       in normal flow (no absolute offset, no extra top margin) */
    .sidebar-bottom {
      grid-row: 3;
      position: relative !important;
      bottom: auto !important;
      left: auto !important;
      width: 100%;
      min-height: 0;
      margin: 0 !important;
      overflow: visible;
    }
  }
`;

let backendInfoCache: string | undefined;
let backendEnvCache: string | undefined;

const SideBar = ({ isVisible }: SideBarProps) => {
  const {
    selectedStore,
    stores,
    selectStore,
  } = useStores();

  const {
    selectedTerritory,
    territories,
    selectTerritory,
  } = useTerritories();

  const {
    showModal,
    closeModal,
    isModalOpen,
  } = useModals();

  const modeType = useSelector(
    (state: RootState) => state?.mode.value
  );

  const dispatch = useDispatch();
  const sideBarRef = useRef<any>(null);
  const optionsCommercial = useOptionCommercial();

  const onStoreSelect = (storeId: string) => {
    const matchingTerritoryKey = territories.find(
      (territory) =>
        territory.stores?.some(
          (territoryStore) =>
            territoryStore.storeKey === storeId
        )
    )?.key;

    if (matchingTerritoryKey) {
      selectTerritory(matchingTerritoryKey);
    }

    selectStore(storeId);
    closeModal();
    handleNav();

    const isOnCommercialLiveOrYesterday =
      location.pathname === "/commercial/live" ||
      location.pathname === "/commercial/yesterday";

    if (isOnCommercialLiveOrYesterday) {
      const store = stores.find(
        (s) => s.storeKey === storeId
      );

      const open = isStoreOpen(
        store?.openingTime,
        store?.closingTime
      );

      const livePath = optionsCommercial.find(
        (opt: any) => opt.range === Range.Direct
      );

      navigate(
        open && !!livePath
          ? livePath?.value
          : optionsCommercial[0]?.value
      );
    }
  };

  const onTerritorySelect = (territoryKey: string) => {
    const firstStoreKey = territories
      .find((territory) => territory.key === territoryKey)
      ?.stores
      ?.map((territoryStore) =>
        stores.find((store) => store.storeKey === territoryStore.storeKey)
      )
      .filter((store): store is NonNullable<typeof store> => Boolean(store))
      .sort((a, b) => a.storeName.localeCompare(b.storeName))?.[0]?.storeKey ?? null;

    selectTerritory(territoryKey);
    selectStore(firstStoreKey);
    closeModal();
    handleNav();
  };

  const openStoreSelector = (mode: "region" | "store") => {
    showModal({
      modalToShow: "STORE_SELECTOR_MODAL",
      modalProps: {
        onCancel: closeModal,
        storeArray: mode === 'store' ? stores : territories,
        onSelect: mode === 'store' ? onStoreSelect : onTerritorySelect,
        selectedStore: mode === 'store' ? selectedStore : selectedTerritory,
        mode: mode === 'store' ? 'store' : 'region',
      },
    });
  };

  const { user, isAuthenticated, logout: authLogout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const showHiddenMenu = useDisplayHiddenMenu();
  const isCaLiveActive = useIsCaLiveRoute();
  const isDashboardActive = useIsDashboardRoute();
  const isOnGlobalPage = isCaLiveActive || isDashboardActive;

  const { getKpiRange } = useRange();
  const regionOptions = useOptionRegion();
  const BE_URL = window?.config?.CREDENTIALS?.REACT_BE_APP_URL;
  const isSaml = !!BE_URL && window.location.href.includes(BE_URL);
  const switchMode = (mode: "region" | "store" | "alert") => {
    dispatch(handleModeSlice(mode));
    sessionStorage.setItem("mode", mode);
    document.documentElement.style.overflow =
    isStoreOrAlertMode(mode) ? "" : "hidden";
    
    let targetPath = "/commercial";
    if (isAlertMode(mode)) {
      targetPath = "/alert";
    } else if (isRegionMode(mode)) {
      const regionRange = getKpiRange("kpi_region");
      targetPath = regionRange 
        ? (regionOptions as any).find((opt: any) => opt.range === regionRange)?.value || "/region/direct"
        : "/region/direct";
    }
    
    navigate(targetPath);
    handleNav();
  };

  const logout = async () => {
    await authLogout();
  };

  const cssHandler = isVisible ? "show_side_bar side_bar" : "side_bar";

  const isUserFranchise = (): boolean => {
    const userRoles = user?.fr_cockpit_360 as string[];
    if (!Array.isArray(userRoles)) {
      return false;
    }
    return window?.config?.CREDENTIALS?.USER_FRANCHISE.some((role: string) => userRoles.includes(role));
  };

  const hasAccessToCommunication = (): boolean => {
    const frCockpitRoles = user?.fr_cockpit_360;
    return Array.isArray(frCockpitRoles) && frCockpitRoles.includes("publicateur_communication");
  };

  const hasCaLiveAccess = (): boolean =>
    [user?.roles, user?.fr_cockpit_360].some(
      (userRoles) =>
        Array.isArray(userRoles) &&
        userRoles.some((role) => CA_LIVE_ALLOWED_ROLES.includes(role))
    );

  const { t, i18n } = useTranslation();
  const {isModuleExists} = useKpiVisibility();

  const [alertSeries, setAlertSeries] = useState<AlertSerie[] | undefined>(undefined);
  const [backendVersion, setBackendVersion] = useState<string | undefined>(backendInfoCache);
  const [backendEnv, setBackendEnv] = useState<string | undefined>(backendEnvCache);
  const [isCommunicationOpen, setIsCommunicationOpen] = useState(false);
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);

  const handleNav = () => {
    dispatch(handleSidebar());
  };

  const handleLanguageSave = async (selectedLanguage: string) => {
    try {
      localStorage.setItem("userLang", selectedLanguage);
      await i18n.changeLanguage(selectedLanguage);
    } catch (error) {
      console.error("Failed to change language:", error);
    }
  };

  useEffect(() => {
    if (backendInfoCache !== undefined) return;

    let active = true;

    fetchAppVersion()
    .then((res) => {
      if(!res)
        throw new Error("Failed to fetch version");

      backendInfoCache = res.version;
      backendEnvCache = res.env;

      setBackendVersion(res.version);
      setBackendEnv(res.env);
    })
    .catch(() => {
      if (!active) return;
      backendInfoCache = "";
      backendEnvCache = "";
    });

    return () => {
      active = false;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!selectedStore?.storeKey) {
      return;
    }

    let active = true;
    if(isModuleExists("Alerting")) {
      fetchAlertsWithStoreKey(`/alerts?range=${Range.CurrentMonth}`, selectedStore.storeKey as string)
        .then(({ data }) => {
          if (active) {
            setAlertSeries(data);
          }
        })
        .catch(() => {
          if (active) {
            setAlertSeries(undefined);
          }
        });
    }

    return () => {
      active = false;
    };
  }, [selectedStore?.storeKey, isModuleExists]);

  const remainingAlertsCount = useMemo(() => {
    if (!alertSeries) return 0;

    return alertSeries.reduce((acc, serie) =>
      serie.status === AlertStatus.NEW ||
      serie.status === AlertStatus.TOADJUST ||
      serie.status === AlertStatus.IGNORED
        ? acc + 1
        : acc, 0);
  }, [alertSeries]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // If the sidebar is open and the click target is outside the sidebar ref, close it
      if (isVisible && sideBarRef.current && !sideBarRef.current.contains(event.target as Node)) {
        
        if (isModalOpen || isCommunicationOpen || isLanguageModalOpen) {
          return;
        }

        let targetElement = event.target as Element | null;
        if (targetElement?.nodeType === Node.TEXT_NODE) {
          targetElement = targetElement.parentElement;
        }
        // Ignore clicks inside modals to preserve the "close on select" behavior for territories/stores
        if (targetElement?.closest?.('[role="dialog"], [class*="modal" i], [class*="overlay" i], [class*="backdrop" i]')) {
          return;
        }

        dispatch(handleSidebar());
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isVisible, dispatch, isModalOpen, isCommunicationOpen, isLanguageModalOpen]);

  return (
    <>
      <StyledSideBarBox
        ref={sideBarRef}
        data-testid="side-bar"
        className={cssHandler}
      >
        <Box
          className="sidebar-inner"
          width={"100%"}
          height={"100%"}
          position={"relative"}
          display="flex"
          flexDirection="column"
        >
          {/* ==================================================
              HEADER
              ================================================== */}

          <Box
            p={2}
            width={"100%"}
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"center"}
            style={{ flexShrink: 0 }}
            className="sidebar-header"
          >
            <Text
              bold
              size={"l"}
              tagStyle={"h4"}
            >
              {t("common.menu")}
            </Text>

            <IconButtonCustom
              icon="close"
              tone="loyalty"
              variation={"transparent"}
              size={"m"}
              $active={true}
              onClick={handleNav}
            />
          </Box>

          {/* ==================================================
              NORMAL MENU
              PORTRAIT / DESKTOP
              (main items only - the bottom group is a SIBLING
              rendered right after this element)
              ================================================== */}

          <Box className="sidebar-default-menu">
            {showHiddenMenu && (
              <>
                <BoxCustom
                  p={2}
                  width={"100%"}
                  display={"flex"}
                  justifyContent={"flex-start"}
                  alignItems={"center"}
                  data-testid="dashboard-sidebar-item"
                  onClick={() => {
                    navigate("/dashboard");
                    handleNav();
                  }}
                >
                  <IconButtonCustom
                    icon="home"
                    tone={"accent"}
                    variation={"transparent"}
                    size={"m"}
                    $active={isDashboardActive}
                  />

                  <Text
                    size={"l"}
                    color={
                      isDashboardActive
                        ? "#0970e6"
                        : ""
                    }
                  >
                    Accueil
                  </Text>
                </BoxCustom>

                <Box px={2}>
                  <Line />
                </Box>
              </>
            )}

            {/* CA LIVE */}
            {hasCaLiveAccess() && (
              <>
                <BoxCustom
                  p={2}
                  width={"100%"}
                  display={"flex"}
                  justifyContent={"flex-start"}
                  alignItems={"center"}
                  data-testid="ca-live-sidebar-item"
                  onClick={() => {
                    navigate("/ca-live");
                    handleNav();
                  }}
                >
                  <IconButtonCustom
                    icon="radio"
                    tone={"accent"}
                    variation={"transparent"}
                    size={"m"}
                    $active={isCaLiveActive}
                  />

                  <Text
                    size={"l"}
                    color={
                      isCaLiveActive
                        ? "#0970e6"
                        : ""
                    }
                  >
                    CA Live
                  </Text>
                </BoxCustom>

                <Box px={2}>
                  <Line />
                </Box>
              </>
            )}

            {/* STORE */}
            <BoxCustom
              p={2}
              width={"100%"}
              display={"flex"}
              justifyContent={"flex-start"}
              alignItems={"center"}
              onClick={() =>
                switchMode("store")
              }
            >
              <IconButtonCustom
                icon="store"
                tone={"accent"}
                variation={"transparent"}
                size={"m"}
                $active={
                  isStoreMode(modeType) && !isOnGlobalPage
                }
              />

              <Text
                size={"l"}
                color={
                  isStoreMode(modeType) && !isOnGlobalPage
                    ? "#0970e6"
                    : ""
                }
              >
                {t(
                  "common.vision_magasin"
                )}
              </Text>
            </BoxCustom>

            <Box px={2}>
              <Line />
            </Box>

            {/* REGION / TERRITORY */}
            {(
              isEmailInWhitelist(
                "TERRITORY_WHITE_LIST",
                user
              ) ||
              !isUserFranchise()
          )
          && isModuleExists("Territoire") && (
            <BoxCustom
              p={2}
              width={"100%"}
              display={"flex"}
              alignItems={"center"}
              onClick={() => switchMode("region")}
            >
              <IconButtonCustom
                icon="map"
                tone={"accent"}
                variation={"transparent"}
                size={"m"}
                $active={isRegionMode(modeType) && !isOnGlobalPage}
              />
              <Text size={"l"} color={(isRegionMode(modeType) && !isOnGlobalPage) ? "#0970e6" : ""}>
                {selectedStore?.storeType === "market" || selectedTerritory?.type !== "territory"
                  ? t('common.vision_region')
                  : t('common.vision_territoire')}
              </Text>
            </BoxCustom>
          )}
          <Box px={2}>
            <Line />
          </Box>
          {(
              isEmailInWhitelist("ALERT_WHITE_LIST", user)
              &&
              !isUserFranchise()
            ) &&
              isModuleExists(
                "Alerting"
              ) && (
                <BoxCustom
                  p={2}
                  width={"100%"}
                  display={"flex"}
                  justifyContent={"flex-start"}
                  alignItems={"center"}
                  onClick={() =>
                    switchMode("alert")
                  }
                >
                  <IconButtonCustom
                    icon="megaphone"
                    tone={"accent"}
                    variation={
                      "transparent"
                    }
                    size={"m"}
                    $active={
                      isAlertMode(modeType) && !isOnGlobalPage
                    }
                  />

                  <Text
                    size={"l"}
                    color={
                      isAlertMode(modeType) && !isOnGlobalPage
                        ? "#0970e6"
                        : ""
                    }
                  >
                    {t(
                      "common.vision_alerte"
                    )}

                    {remainingAlertsCount >
                      0 && (
                      <span
                        style={{
                          fontWeight:
                            "bold",
                          border:
                            "1px solid",
                          marginLeft:
                            "5px",
                          padding:
                            "0 3px",
                          fontSize:
                            "12px",
                        }}
                      >
                        {
                          remainingAlertsCount
                        }
                      </span>
                    )}
                  </Text>
                </BoxCustom>
              )}
          </Box>

          {/* ==================================================
              BOTTOM MENU
              Communication / Language / Territory /
              Store / Logout / Version

              SIBLING of .sidebar-default-menu (NOT a child).
              - mobile portrait: its own grid row, always visible
              - desktop / tablet: pinned to the bottom edge of
                .sidebar-inner (position rules live in the CSS)
              - mobile landscape: hidden (landscape menu is used)
              ================================================== */}

          <Box className="sidebar-bottom">
            {/* COMMUNICATION */}
            {hasAccessToCommunication() && (
              <Box
                px={0}
                width={"100%"}
              >
                <Box
                  px={2}
                  width={"100%"}
                >
                  <Line />
                </Box>

                <BoxCustom
                  p={2}
                  width={"100%"}
                  display={"flex"}
                  justifyContent={
                    "flex-start"
                  }
                  alignItems={"center"}
                  data-testid="communication-button"
                  onClick={() => {
                    dispatch(
                      handleSidebar()
                    );
                    setIsCommunicationOpen(
                      true
                    );
                  }}
                >
                  <Box
                    mr={1}
                    display="flex"
                  >
                    <CommIcon
                      height={28}
                      width={28}
                    />
                  </Box>

                  <Text size={"m"}>
                    {t(
                      "common.communication"
                    )}
                  </Text>
                </BoxCustom>
              </Box>
            )}

            {/* LANGUAGE */}
            {isModuleExists(
              "LanguageSelection"
            ) && (
              <Box
                px={0}
                width={"100%"}
              >
                <Box
                  px={2}
                  width={"100%"}
                >
                  <Line />
                </Box>

                <BoxCustom
                  p={2}
                  width={"100%"}
                  display={"flex"}
                  justifyContent={
                    "flex-start"
                  }
                  alignItems={"center"}
                  data-testid="language-button"
                  onClick={() => {
                    dispatch(
                      handleSidebar()
                    );
                    setIsLanguageModalOpen(
                      true
                    );
                  }}
                >
                  <Box
                    width={35}
                    display="flex"
                    justifyContent="center"
                  >
                    {i18n.language ===
                      "fr" && (
                      <FrIcon
                        height={22}
                        width={22}
                      />
                    )}

                    {i18n.language ===
                      "en" && (
                      <EngIcon
                        height={22}
                        width={22}
                      />
                    )}

                    {i18n.language ===
                      "nl" && (
                      <DutchIcon
                        height={22}
                        width={22}
                      />
                    )}
                  </Box>

                  <Text
                    size={"m"}
                    style={{
                      marginLeft:
                        "5px",
                    }}
                  >
                    {getLanguageLabel(
                      i18n.language
                    )}
                  </Text>
                </BoxCustom>
              </Box>
            )}

            {/* TERRITORY */}
            {isModuleExists(
              "Territoire"
            ) &&
              (
                isEmailInWhitelist(
                  "TERRITORY_WHITE_LIST",
                  user
                ) ||
                !isUserFranchise()
              ) && (
                <>
                  <Box
                    px={2}
                    width={"100%"}
                  >
                    <Line />
                  </Box>

                  <BoxCustom
                    p={2}
                    width={"100%"}
                    display={"flex"}
                    justifyContent={"flex-start"}
                    alignItems={"center"}
                    onClick={() =>
                      (territories?.length ??
                        0) > 1 &&
                      openStoreSelector(
                        "region"
                      )
                    }
                    style={{
                      cursor:
                        (territories?.length ??
                          0) <= 1
                          ? "default"
                          : "pointer",
                    }}
                  >
                    <IconButtonCustom
                      icon="map"
                      tone={
                        "accent"
                      }
                      variation={
                        "transparent"
                      }
                      size={"m"}
                    />

                    <SafeText
                      size={"s"}
                      style={{
                        pointerEvents:
                          "none",
                      }}
                    >
                      {
                        selectedTerritory?.name
                      }
                    </SafeText>

                    {(territories?.length ??
                      0) > 1 && (
                      <Box
                        ml={"auto"}
                        display="flex"
                      >
                        <CIconCustom
                          name="replace-horizontal"
                        />
                      </Box>
                    )}
                  </BoxCustom>
                </>
              )}

            {/* STORE */}
            <Box
              px={2}
              width={"100%"}
            >
              <Line />
            </Box>

            <BoxCustom
              p={2}
              width={"100%"}
              display={"flex"}
              justifyContent={"flex-start"}
              alignItems={"center"}
              onClick={() =>
                (stores?.length ?? 0) >
                  1 &&
                openStoreSelector(
                  "store"
                )
              }
              style={{
                cursor:
                  (stores?.length ?? 0) <=
                  1
                    ? "default"
                    : "pointer",
              }}
            >
              {showIcon(
                selectedStore,
                "store",
                {
                  width: 40,
                  height: 30,
                }
              )}

              <SafeText
                size={"s"}
                style={{
                  marginLeft: "3px",
                  pointerEvents:
                    "none",
                }}
              >
                {
                  selectedStore?.storeName
                }
              </SafeText>

              {(stores?.length ?? 0) >
                1 && (
                <Box
                  ml={"auto"}
                  display="flex"
                >
                  <CIconCustom
                    name="replace-horizontal"
                  />
                </Box>
              )}
            </BoxCustom>

            {/* LOGOUT */}
            <Box
              px={2}
              width={"100%"}
            >
              <Line />
            </Box>

            {!isSaml && (
              <BoxCustom
                p={2}
                width={"100%"}
                display={"flex"}
                justifyContent={"flex-start"}
                alignItems={"center"}
                onClick={logout}
                data-testid="logout-buttton"
              >
                <Box
                  mr={1}
                  display="flex"
                >
                  <LogOutIcon
                    height={22}
                    width={22}
                  />
                </Box>

                <Text
                  size={"m"}
                  style={{
                    color: "red",
                  }}
                >
                  {t(
                    "common.se_deconnecter"
                  )}
                </Text>
              </BoxCustom>
            )}

            {/* DESKTOP / PORTRAIT VERSION */}
            <Box
              p={2}
              width={"100%"}
              display={"flex"}
              backgroundColor={
                "#F5F5F5"
              }
              style={{
                cursor: "text",
              }}
            >
              <Text size="s">
                {`${t(
                  "common.app_version"
                )} : ${
                  backendEnv || ""
                } - ${
                  backendVersion || ""
                }`}
              </Text>
            </Box>
          </Box>

          {/* ==================================================
              MOBILE LANDSCAPE MENU
              ================================================== */}

          <Box className="sidebar-landscape-menu">

            <Box className="sidebar-landscape-left">
              {showHiddenMenu && (
              <>
                <BoxCustom
                  p={2}
                  width={"100%"}
                  display={"flex"}
                  justifyContent={"flex-start"}
                  alignItems={"center"}
                  data-testid="dashboard-sidebar-item"
                  onClick={() => {
                    navigate("/dashboard");
                    handleNav();
                  }}
                >
                  <IconButtonCustom
                    icon="home"
                    tone={"accent"}
                    variation={"transparent"}
                    size={"m"}
                    $active={isDashboardActive}
                  />

                  <Text
                    size={"l"}
                    color={
                      isDashboardActive
                        ? "#0970e6"
                        : ""
                    }
                  >
                    Accueil
                  </Text>
                </BoxCustom>

                <Box px={2}>
                  <Line />
                </Box>
              </>
            )}
              {/* CA LIVE */}
              {hasCaLiveAccess() && (
                <>
                  <BoxCustom
                    className="sidebar-landscape-item"
                    display={"flex"}
                    justifyContent={
                      "flex-start"
                    }
                    alignItems={"center"}
                    data-testid="ca-live-sidebar-item"
                    onClick={() => {
                      navigate(
                        "/ca-live"
                      );
                      handleNav();
                    }}
                  >
                    <IconButtonCustom
                      icon="radio"
                      tone={"accent"}
                      variation={"transparent"}
                      size={"m"}
                      $active={isCaLiveActive}
                    />

                    <Text
                      className="sidebar-landscape-text"
                      size={"l"}
                      color={
                        isCaLiveActive
                          ? "#0970e6"
                          : ""
                      }
                    >
                      CA Live
                    </Text>
                  </BoxCustom>

                  <Box className="sidebar-landscape-divider" />
                </>
              )}

              {/* MAGASIN */}
              <BoxCustom
                className="sidebar-landscape-item"
                display={"flex"}
                justifyContent={
                  "flex-start"
                }
                alignItems={"center"}
                onClick={() =>
                  switchMode("store")
                }
              >
                <IconButtonCustom
                  icon="store"
                  tone={"accent"}
                  variation={
                    "transparent"
                  }
                  size={"m"}
                  $active={
                    isStoreMode(modeType) && !isOnGlobalPage
                  }
                />

                <Text
                  className="sidebar-landscape-text"
                  size={"l"}
                  color={
                    isStoreMode(modeType) && !isOnGlobalPage
                      ? "#0970e6"
                      : ""
                  }
                >
                  {t(
                    "common.vision_magasin"
                  )}
                </Text>
              </BoxCustom>

              <Box className="sidebar-landscape-divider" />

              {/* TERRITOIRE */}
              {(
                isEmailInWhitelist(
                  "TERRITORY_WHITE_LIST",
                  user
                ) ||
                !isUserFranchise()
              ) &&
                isModuleExists(
                  "Territoire"
                ) && (
                  <>
                    <BoxCustom
                      className="sidebar-landscape-item"
                      display={"flex"}
                      justifyContent={
                        "flex-start"
                      }
                      alignItems={
                        "center"
                      }
                      onClick={() =>
                        switchMode(
                          "region"
                        )
                      }
                    >
                      <IconButtonCustom
                        icon="map"
                        tone={
                          "accent"
                        }
                        variation={
                          "transparent"
                        }
                        size={"m"}
                        $active={
                          isRegionMode(modeType) && !isOnGlobalPage
                        }
                      />

                      <Text
                        className="sidebar-landscape-text"
                        size={"l"}
                        color={
                          isRegionMode(modeType) && !isOnGlobalPage
                            ? "#0970e6"
                            : ""
                        }
                      >
                        {selectedTerritory?.type ===
                        "territory"
                          ? t(
                              "common.vision_territoire"
                            )
                          : t(
                              "common.vision_region"
                            )}
                      </Text>
                    </BoxCustom>

                    <Box className="sidebar-landscape-divider" />
                  </>
                )}

              {/* ALERTES */}
              {(
                isEmailInWhitelist(
                  "ALERT_WHITE_LIST",
                  user
                ) &&
                !isUserFranchise()
              ) &&
                isModuleExists(
                  "Alerting"
                ) && (
                  <BoxCustom
                    className="sidebar-landscape-item"
                    display={"flex"}
                    justifyContent={
                      "flex-start"
                    }
                    alignItems={
                      "center"
                    }
                    onClick={() =>
                      switchMode(
                        "alert"
                      )
                    }
                  >
                    <IconButtonCustom
                      icon="megaphone"
                      tone={
                        "accent"
                      }
                      variation={
                        "transparent"
                      }
                      size={"m"}
                      $active={
                        isAlertMode(modeType) && !isOnGlobalPage
                      }
                    />

                    <Text
                      className="sidebar-landscape-text"
                      size={"l"}
                      color={
                        isAlertMode(modeType) && !isOnGlobalPage
                          ? "#0970e6"
                          : ""
                      }
                    >
                      {t(
                        "common.vision_alerte"
                      )}

                      {remainingAlertsCount >
                        0 && (
                        <span
                          style={{
                            fontWeight:
                              "bold",
                            border:
                              "1px solid",
                            marginLeft:
                              "5px",
                            padding:
                              "0 3px",
                            fontSize:
                              "12px",
                          }}
                        >
                          {
                            remainingAlertsCount
                          }
                        </span>
                      )}
                    </Text>
                  </BoxCustom>
                )}
            </Box>

            <Box className="sidebar-landscape-right">
              {/* TERRITORY / REGION NAME */}
              {isModuleExists(
                "Territoire"
              ) &&
                (
                  isEmailInWhitelist(
                    "TERRITORY_WHITE_LIST",
                    user
                  ) ||
                  !isUserFranchise()
                ) && (
                  <>
                    <BoxCustom
                      className="sidebar-landscape-item"
                      display={"flex"}
                      justifyContent={
                        "flex-start"
                      }
                      alignItems={
                        "center"
                      }
                      onClick={() =>
                        (territories?.length ??
                          0) > 1 &&
                        openStoreSelector(
                          "region"
                        )
                      }
                      style={{
                        cursor:
                          (territories?.length ??
                            0) <= 1
                            ? "default"
                            : "pointer",
                      }}
                    >
                      <IconButtonCustom
                        icon="map"
                        tone={
                          "accent"
                        }
                        variation={
                          "transparent"
                        }
                        size={"m"}
                      />

                      <SafeText
                        className="sidebar-landscape-text"
                        size={"s"}
                        style={{
                          pointerEvents:
                            "none",
                        }}
                      >
                        {
                          selectedTerritory?.name
                        }
                      </SafeText>

                      {(territories?.length ??
                        0) > 1 && (
                        <Box
                          ml={"auto"}
                          display="flex"
                        >
                          <CIconCustom
                            name="replace-horizontal"
                          />
                        </Box>
                      )}
                    </BoxCustom>

                    <Box className="sidebar-landscape-divider" />
                  </>
                )}

              {/* STORE NAME */}
              <BoxCustom
                className="sidebar-landscape-item"
                display={"flex"}
                justifyContent={
                  "flex-start"
                }
                alignItems={"center"}
                onClick={() =>
                  (stores?.length ?? 0) >
                    1 &&
                  openStoreSelector(
                    "store"
                  )
                }
                style={{
                  cursor:
                    (stores?.length ?? 0) <=
                    1
                      ? "default"
                      : "pointer",
                }}
              >
                {showIcon(
                  selectedStore,
                  "store",
                  {
                    width: 40,
                    height: 30,
                  }
                )}

                <SafeText
                  className="sidebar-landscape-text"
                  size={"s"}
                  style={{
                    marginLeft: "0px",
                    pointerEvents:
                      "none",
                  }}
                >
                  {
                    selectedStore?.storeName
                  }
                </SafeText>

                {(stores?.length ?? 0) >
                  1 && (
                  <Box
                    ml={"auto"}
                    display="flex"
                  >
                    <CIconCustom
                      name="replace-horizontal"
                    />
                  </Box>
                )}
              </BoxCustom>

              <Box className="sidebar-landscape-divider" />

              {/* LOGOUT - BOTTOM OF RIGHT COLUMN */}
              {!isSaml && (
                <BoxCustom
                  className="sidebar-landscape-item sidebar-landscape-logout"
                  display={"flex"}
                  justifyContent={
                    "flex-start"
                  }
                  alignItems={"center"}
                  onClick={logout}
                  data-testid="logout-buttton"
                >
                  <Box
                    mr={1}
                    display="flex"
                  >
                    <LogOutIcon
                      height={22}
                      width={22}
                    />
                  </Box>

                  <Text
                    className="sidebar-landscape-text"
                    size={"m"}
                    style={{
                      color: "red",
                    }}
                  >
                    {t(
                      "common.se_deconnecter"
                    )}
                  </Text>
                </BoxCustom>
              )}
            </Box>
          </Box>

          {/* ==================================================
              MOBILE LANDSCAPE VERSION
              Full width / right aligned
              ================================================== */}

          <Box className="sidebar-landscape-version">
            <Text
              className="sidebar-version-text"
              size="s"
            >
              {`${t(
                "common.app_version"
              )} : ${
                backendEnv || ""
              } - ${
                backendVersion || ""
              }`}
            </Text>
          </Box>
        </Box>
      </StyledSideBarBox>

      {/* ======================================================
          MODALS
          ====================================================== */}

      <CommunicationModal
        open={isCommunicationOpen}
        onClose={() =>
          setIsCommunicationOpen(false)
        }
      />

      <SelectLanguageModal
        open={isLanguageModalOpen}
        onClose={() => setIsLanguageModalOpen(false)}
        onSave={handleLanguageSave}
      />
    </>
  );
};

export default SideBar;